// AdvisorFeeCalculator.jsx

import React, { useState } from 'react';
import {
  Container,
  Tabs,
  Tab,
  Box,
} from '@mui/material';
import { motion } from 'framer-motion';
import { TrendingUp as TrendingUpIcon, AttachMoney as AttachMoneyIcon, Assessment as AssessmentIcon, ShowChart as ShowChartIcon } from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { blue, lightBlue, blueGrey } from '@mui/material/colors';
import { usePDF } from 'react-to-pdf';

// Import the separate calculator components
import FeeCalculator from './FeeCalculator';
import ProfitabilityCalculator from './ProfitabilityCalculator';
import RetirementReadinessCalculator from './RetirementReadinessCalculator';
import RevenueForecastCalculator from './RevenueForecastCalculator';

// Update the custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: blue[700],
      light: blue[300],
      dark: blue[900],
    },
    secondary: {
      main: blueGrey[600],
      light: blueGrey[400],
      dark: blueGrey[800],
    },
    background: {
      default: lightBlue[50],
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: "'Poppins', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 15,
          },
        },
      },
    },
  },
});

const AdvisorFeeCalculator = () => {
  // Tab management
  const [activeTab, setActiveTab] = useState(0);

  // Add this function to generate PDF
  const { targetRef } = usePDF({ filename: 'advisor-fee-calculation.pdf' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ paddingTop: '3rem', paddingBottom: 6 }}>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Tab Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  minWidth: 200,
                },
              }}
            >
              <Tab 
                icon={<TrendingUpIcon />} 
                label="Fee Calculator" 
                iconPosition="start"
              />
              <Tab 
                icon={<AttachMoneyIcon />} 
                label="Profitability Calculator" 
                iconPosition="start"
              />
              <Tab 
                icon={<AssessmentIcon />} 
                label="Retirement Readiness" 
                iconPosition="start"
              />
              <Tab 
                icon={<ShowChartIcon />} 
                label="Revenue Forecast" 
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Wrap the content to be included in the PDF with a div and the ref */}
          <div ref={targetRef}>
            {activeTab === 0 && <FeeCalculator />}
            {activeTab === 1 && <ProfitabilityCalculator />}
            {activeTab === 2 && <RetirementReadinessCalculator />}
            {activeTab === 3 && <RevenueForecastCalculator />}
          </div>
        </motion.div>
      </Container>
    </ThemeProvider>
  );
};

export default AdvisorFeeCalculator;